import { RadioGroup } from '@mantine/core';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { Input } from 'src/design/atoms/Input';
import { Radio } from 'src/design/atoms/Radio';
import { TextInput } from 'src/design/atoms/TextInput';
import {
  IconBarrierBlock,
  IconClockPlus,
  IconCurrencyDollar,
  IconExclamationCircle,
} from '@tabler/icons-react';
import React from 'react';

import {
  AttachmentDto,
  JobDto,
  OrganizationRoleDto,
  PostDocumentDto,
  PostRequestDto,
  UserCoreDto,
  UserDto,
  PostLinkItemDto,
  PostDto,
} from 'src/api/FiretrolApiClient';
import {
  useCreatePost,
  useUpdatePost,
} from 'src/api/queries/posts/usePostMutations';
import { JobUserSelect } from 'src/app/selectors/JobUserSelectSingle';
import { useAuth } from 'src/hooks/useAuth';
import { useJobs } from 'src/hooks/useJobs';
import { ModalButton } from 'src/design/molecules/ModalButton';
import { DatePicker } from 'src/design/atoms/DatePicker';
import { MentionInput } from 'src/routes/Jobs/ActivityFeed/components/PostCard/components/MentionInput';
import { PostActions } from 'src/routes/Jobs/ActivityFeed/components/PostCard/components/PostActions';
import { PostFiles } from 'src/routes/Jobs/ActivityFeed/components/PostCard/components/PostFiles';
import { PostLinks } from 'src/routes/Jobs/ActivityFeed/components/PostCard/components/PostLinks';

interface PostFormProps {
  handleClose: () => void;
  onPostCreated: () => void;
  initialValues?: PostDto | null;
  job?: JobDto;
  jobId?: number;
}

export const PostForm: React.FC<PostFormProps> = ({
  handleClose,
  onPostCreated,
  initialValues,
  job,
  jobId: jobIdProp,
}) => {
  const { user } = useAuth();
  const [files, setFiles] = React.useState<AttachmentDto[]>(
    initialValues?.files ?? [],
  );
  const [filesToRemove, setFilesToRemove] = React.useState<string[]>([]);
  const [links, setLinks] = React.useState<PostLinkItemDto[]>(
    initialValues
      ? [
          ...initialValues.meetings,
          ...initialValues.dailys,
          ...initialValues.rfis,
          ...initialValues.changes,
        ]
      : [],
  );
  const [documents, setDocuments] = React.useState<PostDocumentDto[]>(
    initialValues?.documents ?? [],
  );
  const [people, setPeople] = React.useState<UserCoreDto[]>(
    initialValues?.mentions ?? [],
  );
  const [groups, setGroups] = React.useState<OrganizationRoleDto[]>(
    initialValues?.groups ?? [],
  );
  const [subject, setSubject] = React.useState(
    initialValues ? initialValues.subject : '',
  );
  const [content, setContent] = React.useState(
    initialValues ? initialValues.content : '',
  );
  const [visibility, setVisibility] = React.useState(
    initialValues ? initialValues.visibility : 'public',
  );
  const [critical, setCritical] = React.useState(
    initialValues ? initialValues.critical : false,
  );
  const [constraint, setConstraint] = React.useState(
    initialValues ? initialValues.constraint : false,
  );
  const [ctc, setCTC] = React.useState(
    initialValues ? initialValues.ctc : false,
  );
  const [cost, setCost] = React.useState(
    initialValues ? initialValues.cost : false,
  );
  const [date, setDate] = React.useState<Date | null>(
    initialValues ? initialValues.date : null,
  );
  const [assigned, setAssigned] = React.useState<UserCoreDto | null>(
    initialValues?.assignedTo ?? null,
  );
  const [plainTextContent, setPlainTextContent] = React.useState(
    initialValues ? initialValues.plainTextContent : '',
  );

  const { selectedJob, users } = useJobs();
  const sJob = job || selectedJob;
  const effectiveJobId = jobIdProp ?? sJob?.id;

  const createPost = useCreatePost(effectiveJobId ?? -1);
  const updatePost = useUpdatePost(
    initialValues?.jobId ?? effectiveJobId ?? -1,
  );

  const onFileUploaded = (file: AttachmentDto) => {
    setFiles((prevState) => [...prevState, file]);
  };

  const onFileRemoved = (file: AttachmentDto) => {
    setFiles((prevState) => prevState.filter((a) => a.id !== file.id));
    setFilesToRemove((prevState) => [...prevState, file.id]);
  };

  const onDocumentRemoved = (doc: PostDocumentDto) => {
    setDocuments((prevState) => prevState.filter((a) => a.id !== doc.id));
  };

  const onLinkSelected = (link: PostLinkItemDto) => {
    setLinks((prevState) => [...prevState, link]);
  };

  const onDocumentSelected = (doc: PostDocumentDto) => {
    setDocuments((prevState) => [...prevState, doc]);
  };

  const onLinkRemoved = (link: PostLinkItemDto) => {
    setLinks((prevState) => prevState.filter((a) => a.id !== link.id));
  };

  const onContentChange = (
    value: string,
    plainTextValue: string,
    peeps: UserDto[],
    roles: OrganizationRoleDto[],
  ) => {
    setContent(value);
    setPeople(peeps);
    setGroups(roles);
    setPlainTextContent(plainTextValue);
  };

  const submitPostForm = async () => {
    if (!effectiveJobId) return;
    const payload: PostRequestDto = {
      subject,
      content,
      plainTextContent,
      visibility,
      critical,
      cost,
      ctc,
      constraint,
      resolved: false,
      date: date ?? undefined,
      assignedTo: assigned ? { id: assigned.id } : undefined,
      mentions: people.map((p) => ({ id: p.id })),
      groups: groups.map((g) => ({ id: g.id })),
      documents: documents.map((d) => ({ id: d.id })),
      files: files.map((f) => ({ id: f.id })),
      filesToRemove,
      links,
    };
    if (initialValues) {
      await updatePost.mutateAsync({
        postId: initialValues.id,
        request: payload,
      });
    } else {
      await createPost.mutateAsync(payload);
    }
    onPostCreated();
    handleClose();
  };

  if (!effectiveJobId || !user) return null;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={5}>
          {user.fullName}
          {sJob?.name ? ` in ${sJob.name}` : ''}
        </Title>

        <Group gap="xs">
          <Tooltip label="CTC">
            <BasicActionIcon
              variant={ctc ? 'filled' : 'outline'}
              onClick={() => setCTC((prevState) => !prevState)}
            >
              <IconCurrencyDollar size={18} />
            </BasicActionIcon>
          </Tooltip>

          <Tooltip label="Critical">
            <BasicActionIcon
              variant={critical ? 'filled' : 'outline'}
              onClick={() => setCritical((prevState) => !prevState)}
            >
              <IconExclamationCircle size={18} />
            </BasicActionIcon>
          </Tooltip>

          <Tooltip label="Constraint">
            <BasicActionIcon
              variant={constraint ? 'filled' : 'outline'}
              onClick={() => setConstraint((prevState) => !prevState)}
            >
              <IconBarrierBlock size={18} />
            </BasicActionIcon>
          </Tooltip>

          <Tooltip label="Cost and Schedule">
            <BasicActionIcon
              variant={cost ? 'filled' : 'outline'}
              onClick={() => setCost((prevState) => !prevState)}
            >
              <IconClockPlus size={18} />
            </BasicActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Input.Wrapper label="Assigned To">
        <JobUserSelect
          hideLabel={true}
          onChange={(value) => setAssigned(value?.user || null)}
          selectedUserId={assigned?.id}
        />
      </Input.Wrapper>

      <DatePicker
        label="Due Date"
        value={date}
        onChange={(value) => setDate(value)}
      />

      <RadioGroup
        value={visibility}
        onChange={(value) => setVisibility(value)}
        label="Visibility"
      >
        <Group mt="xs">
          <Radio label="Public" value="public" />
          <Radio label="Private" value="private" />
        </Group>
      </RadioGroup>

      <TextInput
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Enter subject"
      />

      <MentionInput
        value={content}
        onChange={onContentChange}
        peopleOptions={users}
        id="post-form-mention-input"
      />

      <PostFiles
        files={files}
        documents={documents}
        onFileRemoved={onFileRemoved}
        onDocumentRemoved={onDocumentRemoved}
        jobId={effectiveJobId}
      />

      <PostLinks links={links} onLinkRemoved={onLinkRemoved} />

      <PostActions
        onFileUploaded={onFileUploaded}
        onLinkSelected={onLinkSelected}
        onDocumentSelected={onDocumentSelected}
        jobId={effectiveJobId}
      />

      <Group justify="flex-end">
        <ModalButton variant="light" color="black" onClick={handleClose}>
          Cancel
        </ModalButton>
        <ModalButton
          onClick={submitPostForm}
          loading={createPost.isPending || updatePost.isPending}
          disabled={!subject || !content}
        >
          {initialValues ? 'Update' : 'Post'}
        </ModalButton>
      </Group>
    </Stack>
  );
};
